import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { Category, ClientStore, Offer, OrderItem } from 'src/app/stores/stores';
import { SelectedStoreState } from '../../+state/stores.reducer';
import { getCurrentCartStatus, getCurrentOfferItem, getSelectedStore, getSelectedStoreStatus, getUserLanguage } from '../../+state/stores.selectors';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { CheckoutService } from '../../store-checkout/checkout.service';
import { LocationService } from 'src/app/public/location.service';
import { HelperService } from 'src/app/public/helper.service';
import UrlUtils from 'src/app/stores/utils/url-utils';

@Component({
  selector: 'app-catalog-layout-list',
  templateUrl: './catalog-layout-list.component.html',
  styleUrls: ['./catalog-layout-list.component.scss'],
})
export class CatalogLayoutListComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() menuCategories: Category[];
  @Input() gutterBottom = false;
  @Input() categorySearch: string;
  @Input() selectedCat: number;
  @Input() searchSelectedCatList: number[] = [];
  @Input() cartItems: OrderItem[] = [];
  @Input() selectedStoreLocale: string;
  @Input() selectedStoreCurrency: string;
  @Input() selectedStoreCurrencySymbol: string;
  @Input() showPayOnlineButton: boolean;
  @Input() totalValue: number;
  @Input() contentHeaderHeight: number;
  @Input() selectedCatalog: number;
  @Input() expandCategory: EventEmitter<any>;
  @Input() isIOSApp: boolean;

  @Output() categorySelected = new EventEmitter<{ categoryId: number; toggle: string }>();
  @Output() itemAddedToCart = new EventEmitter<{
    offerId: number;
    categoryId: number;
    inStock: boolean;
    isDirectlyOrderable: boolean;
    isExpandable: boolean;
    isOrderable: boolean;
    basketEnabled: boolean;
  }>();
  @Output() viewOrderDetails = new EventEmitter<OrderItem>();
  @Output() decreaseItemQty = new EventEmitter<OrderItem>();
  @Output() increaseItemQty = new EventEmitter<OrderItem>();
  @Output() productDetailsViewed = new EventEmitter<{
    offerId: any;
    categoryId: any;
    inStock: any;
    isExpandable: any;
    isOrderable: any;
    basketEnabled: any;
  }>();
  @Output() viewOrder = new EventEmitter<any>();
  @Output() onlinePayment = new EventEmitter<void>();

  @ViewChild('catalogContent') catalogContent: ElementRef;
  @ViewChild('cartContent') cartContent: ElementRef;

  isAdminOrderCaptureUpdate: boolean;
  basketEnabled = false;
  orderingEnabled = false;
  isGoApp = false;
  isMobileApp=false;
  currentCartStatus: string;
  cartContentPosition = 400;
  selectedStore$: Observable<ClientStore>;
  selectedStore: ClientStore;
  selectedStoreStatus$: Observable<string>;
  unsubscribe$: Subject<void> = new Subject<void>();
  userLang$: Observable<string>;
  browserTimeZone: string;
  cookieBarHeight: number = 0;
  isCategoryAction = false; 

  constructor(
    private store: Store<SelectedStoreState>,
    public checkoutService: CheckoutService,
    private locationService: LocationService,
    public helper: HelperService,
    private sanitizer: DomSanitizer,
    private _ref: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.isMobileApp = this.helper.isMobileApp();
    this.selectedStore$ = this.store.pipe(select(getSelectedStore));
    this.selectedStoreStatus$ = this.store.pipe(select(getSelectedStoreStatus));
    this.userLang$ = this.store.select(getUserLanguage);
    this.browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.isAdminOrderCaptureUpdate = this.locationService.isAdminOrderCaptureUpdate();
    this.isGoApp = this.helper.isGoApp();
    
    this.selectedStore$.pipe(takeUntil(this.unsubscribe$)).subscribe((selectedStore) => {
      this.selectedStore = selectedStore;
      this.basketEnabled = this.locationService.isBasketEnabled() == null ? 
        this.selectedStore?.settings?.BASKET_ENABLED : 
        this.locationService.isBasketEnabled();
      this.orderingEnabled = selectedStore?.settings?.ENABLE_ORDERING;
    });

    this.store
    .pipe(select(getCurrentCartStatus))
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((cartStatus) => {
      this.currentCartStatus = cartStatus;
    });

    this.expandCategory.subscribe(({ categoryId, toggle }) => this.onExpandCategory(categoryId, toggle));
    this.checkUrl();
  }
  checkUrl() {
    const currentUrl = this.router.url;  // Get the current URL from Router
    this.isCategoryAction = UrlUtils.checkUrl(currentUrl);
  }

  ngAfterViewChecked(): void {
    const stickyCookie = document.getElementById('sticky-cookie');
    const cookieBar = document.getElementById('cookie-bar');

    if ((cookieBar || stickyCookie) && this.cookieBarHeight !== this.helper.getCookieBarHeight()) {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.cookieBarHeight = this.helper.getCookieBarHeight();
        }
        this._ref.detectChanges();
      });
  
      if (cookieBar) resizeObserver?.observe(cookieBar);
      if (stickyCookie) resizeObserver?.observe(stickyCookie);
  
      // Disconnect the observer in ngOnDestroy to prevent memory leaks
      this.unsubscribe$.subscribe(() => {
        resizeObserver.disconnect();
      });
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll($event: any) {
    this.cartContentPosition = this.cartContent?.nativeElement?.getBoundingClientRect()?.top || 400;
  }

  getBackgroundImage(url: any) {
    if (url) {
      return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
    }
    return '';
  }

  isCategoryExpanded(categoryId: number): boolean {
    const queryCatNav = this.catalogContent?.nativeElement?.querySelector(`#category${categoryId}`);
    return queryCatNav?.classList.contains('expanded') || false;
  }

  checkOfferInCart(menuItem) {
    if (!this.cartItems) {
      return {};
    }
    return {
      item: this.cartItems.find((item) => item.offerId === menuItem.offerId),
    };
  }

  isTimeShowDisabled() {
    return this.checkoutService.isTimeShowDisabled();
  }

  checkCategorySearchFilter(items: any) {
    if (items) {
      const tempItems = items.filter((menuItem) => menuItem.name?.toLocaleLowerCase().includes(this.categorySearch?.toLocaleLowerCase()));
      tempItems.forEach((element) => this.searchSelectedCatList.push(element.categoryId));
      return tempItems.length > 0;
    }
  }

  onSelectCategory(categoryId: number, toggle = 'off') {
    this.categorySelected.emit({ categoryId, toggle });
  }

  onExpandCategory(categoryId: number, toggle: string) {
    const queryCatNav = this.catalogContent?.nativeElement?.querySelector(`#category${categoryId}`);
    if (toggle === 'off') {
      queryCatNav?.classList.add('expanded');
    }
    if (toggle === 'toggle') {
      queryCatNav?.classList.toggle('expanded');
    }
  }

  viewProductDetails(offerId: any, categoryId: any, inStock: any, isExpandable: any, isOrderable: any, basketEnabled: any) {
    this.productDetailsViewed.emit({ offerId, categoryId, inStock, isExpandable, isOrderable, basketEnabled });
  }

  onOneClickAddToCart(event, offerId, categoryId, inStock, isDirectlyOrderable, isExpandable, isOrderable, basketEnabled) {
    event.stopPropagation();
    this.itemAddedToCart.emit({ offerId, categoryId, inStock, isDirectlyOrderable, isExpandable, isOrderable, basketEnabled });
  }

  getCartItems(menuItem: any) : OrderItem[] {
    return this.cartItems?.filter((item) => item.offerId === menuItem?.offerId);
  }

  viewOrderItemDetails(item: OrderItem) {
    this.viewOrderDetails.emit(item);
  }

  onDecreaseItemQty(event, item) {
    event.stopPropagation();
    this.decreaseItemQty.emit(item);
  }

  onIncreaseItemQty(event, item) {
    event.stopPropagation();
    this.increaseItemQty.emit(item);
  }

  onViewOrder(event) {
    this.viewOrder.emit(event);
  }

  payOnline() {
    this.onlinePayment.emit();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onOfferImageClick(event, offer) {
    if(this.getCartItems(offer)?.length > 0 || this.getQuantity(this.getCartItems(offer)) > 0) {
      return;
    }

    event.stopPropagation();
    const { offerId, categoryId, inStock, isDirectlyOrderable, isExpandable, isOrderable } = offer;
    this.itemAddedToCart.emit({
      offerId,
      categoryId,
      inStock,
      isDirectlyOrderable,
      isExpandable,
      isOrderable,
      basketEnabled: this.basketEnabled,
    });
  }


  isAllergenSelected(attributes, name) {
    const attr = attributes.find(item => item.key === name);
    return attr ? attr.value : false;
  }

  getQuantity(items) {
    if (items && items.length) {
      return items.reduce((acc, item) => acc + item.quantity, 0);
    }
    return 0;
  }

  centerAlign(): boolean {
    return !this.basketEnabled && !this.isAdminOrderCaptureUpdate
  }
}
